import { defineStore } from "pinia";
import { ref, Ref } from "vue";
import {
  IDayOffOverride,
  INewDayOffOverride,
  INewUser,
  IUpdateUserProfile,
  IUser,
  IUserAssign,
  IUserDayOff,
  IUserFilter,
  IWorkerFilter,
} from "@/features/users/user.types";
import { ITableHeader } from "@/types/table";
import { userService } from "@/features/users/user.service";
import { useNotification } from "@/utils/notification";

const { pushNotification } = useNotification();

export const useUserStore = defineStore("user", () => {
  const userHeaders: ITableHeader[] = [
    {
      name: "id",
      label: "No",
      fitContent: true,
      type: "centert",
    },
    {
      name: "title",
      label: "Full name",
      width: "20%",
    },
    {
      name: "email",
      label: "Email",
      width: "20%",
    },
    {
      name: "phone_number",
      label: "Phone Number",
      width: "15%",
    },
    {
      name: "role",
      label: "Role",
      width: "10%",
    },
    {
      name: "team",
      label: "Team",
      width: "10%",
    },
    {
      name: "off_day",
      label: "Off Day",
      width: "10%",
    },
    {
      name: "actions",
      label: "Actions",
      width: "10%",
    },
  ];

  const commonFilter = ref<IUserFilter>({
    q: "",
    team_id: "",
    shift_template_id: "",
    is_active: true,
    role: "",
  });

  const commonFilterSecondary = ref<IWorkerFilter>({
    q: "",
    team_id: "",
    shift_template_id: "",
    is_active: true,
    available_on: "",
  });

  const clearFilter = () => {
    commonFilter.value = {
      q: "",
      team_id: "",
      shift_template_id: "",
      is_active: true,
      role: "",
    };
  };

  const userProfile = ref<IUser | null>(null);

  async function getUsers(loading: Ref<boolean>): Promise<IUser[]> {
    try {
      loading.value = true;
      const { data } = await userService.getUsers({ ...commonFilter.value });
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function createUser(loading: Ref<boolean>, payload: INewUser) {
    try {
      loading.value = true;
      const { data } = await userService.createUser(payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getWorkers(
    loading: Ref<boolean>,
    filter: IWorkerFilter
  ): Promise<IUser[]> {
    try {
      loading.value = true;
      const { data } = await userService.getWorkers({
        ...commonFilterSecondary.value,
      });

      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function getUserById(
    loading: Ref<boolean>,
    id: string
  ): Promise<IUser> {
    try {
      loading.value = true;
      const { data } = await userService.getUserById(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function deactivateUser(loading: Ref<boolean>, id: string) {
    try {
      loading.value = true;
      const { data } = await userService.deactivateUser(id);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function updateUserProfile(
    loading: Ref<boolean>,
    id: string,
    payload: IUpdateUserProfile
  ) {
    try {
      loading.value = true;
      const { data } = await userService.updateUserProfile(id, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function assignUser(
    loading: Ref<boolean>,
    payload: IUserAssign,
    id: string
  ) {
    try {
      loading.value = true;
      const { data } = await userService.assignUser(id, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function updateDayOff(
    loading: Ref<boolean>,
    id: string,
    payload: IUserDayOff
  ) {
    try {
      loading.value = true;
      const { data } = await userService.updateDayOff(id, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getDayOffOverrides(
    loading: Ref<boolean>,
    userId: string
  ): Promise<IDayOffOverride[]> {
    try {
      loading.value = true;
      const { data } = await userService.getDayOffOverrides(userId);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return [] as any;
    } finally {
      loading.value = false;
    }
  }

  async function createOrUpdateDayOffOverride(
    loading: Ref<boolean>,
    userId: string,
    payload: INewDayOffOverride
  ) {
    try {
      loading.value = true;
      const { data } = await userService.createOrUpdateDayOffOverride(
        userId,
        payload
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function deleteDayOffOverride(
    loading: Ref<boolean>,
    userId: string,
    overrideId: string
  ) {
    try {
      loading.value = true;
      const { data } = await userService.deleteDayOffOverride(
        userId,
        overrideId
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getEffectiveDayOff(
    loading: Ref<boolean>,
    userId: string,
    target_date: string
  ) {
    try {
      loading.value = true;
      const { data } = await userService.getEffectiveDayOff(
        userId,
        target_date
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getUserAvailability(
    loading: Ref<boolean>,
    userId: string,
    target_date: string
  ) {
    try {
      loading.value = true;
      const { data } = await userService.getUserAvailability(
        userId,
        target_date
      );
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function getUserProfile(loading: Ref<boolean>) {
    try {
      loading.value = true;
      const { data } = await userService.getUserProfile();
      userProfile.value = data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(
    loading: Ref<boolean>,
    userId: string,
    payload: any
  ) {
    try {
      loading.value = true;
      const { data } = await userService.updatePassword(userId, payload);
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      pushNotification(message, "error");
      return {} as any;
    } finally {
      loading.value = false;
    }
  }

  return {
    userHeaders,
    commonFilter,
    userProfile,
    clearFilter,
    getUsers,
    createUser,
    getWorkers,
    getUserById,
    deactivateUser,
    updateUserProfile,
    assignUser,
    updateDayOff,
    getDayOffOverrides,
    createOrUpdateDayOffOverride,
    deleteDayOffOverride,
    getEffectiveDayOff,
    getUserAvailability,
    getUserProfile,
    changePassword,
  };
});
